import React, { useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, FileText } from 'lucide-react';
import UserAvatar from './UserAvatar';
import './DesignTaskKanban.css';

const STATE_NAMES = {
  1:  'Initiated',         2:  'Classified',         3:  'Impact Assessment',
  4:  'Drafting',          5:  'Technical Review',   6:  'Independent Verification',
  7:  'Configuration',     8:  'Ready for Release',  9:  'Released',
  10: 'Closed',            11: 'Cancelled',
};

const STATE_SHORT = {
  1: 'Init', 2: 'Class', 3: 'Impact', 4: 'Draft', 5: 'Review', 6: 'Verify',
  7: 'Config', 8: 'Ready', 9: 'Rel.', 10: 'Closed', 11: 'Cancel',
};

// Each state's phase — used for header tinting (so the workflow shape
// is still readable even though every state is its own column).
const STATE_PHASE = {
  1:  'initiation', 2:  'initiation',
  3:  'assessment',
  4:  'drafting',
  5:  'review',     6:  'review',
  7:  'release',    8:  'release',    9:  'release',
  10: 'closed',     11: 'cancelled',
};

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function DesignTaskKanban({ dts }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const boardRef = useRef(null);

  const byState = useMemo(() => {
    const out = {};
    for (let i = 1; i <= 11; i++) out[i] = [];
    for (const dt of dts) {
      if (out[dt.procedureState]) out[dt.procedureState].push(dt);
    }
    return out;
  }, [dts]);

  // Click-to-scroll: snap the board so the chosen column is in view.
  const scrollToState = useCallback((stateNum) => {
    const col = boardRef.current?.querySelector(`[data-state="${stateNum}"]`);
    if (col) col.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }, []);

  return (
    <div className="dtk">

      {/* ── State overview strip — click any cell to scroll to that column */}
      <div className="dtk-strip">
        <div className="dtk-strip-label">State distribution · click to jump</div>
        <div className="dtk-strip-cells">
          {Array.from({ length: 11 }, (_, i) => i + 1).map(stateNum => {
            const count = byState[stateNum].length;
            const phase = STATE_PHASE[stateNum];
            return (
              <button
                key={stateNum}
                type="button"
                className={`dtk-strip-cell dtk-phase-${phase} ${count === 0 ? 'is-empty' : ''}`}
                title={`State ${stateNum} — ${STATE_NAMES[stateNum]}: ${count} ${count === 1 ? 'DT' : 'DTs'}`}
                onClick={() => scrollToState(stateNum)}
              >
                <span className="dtk-strip-state-num">{stateNum}</span>
                <span className="dtk-strip-count">{count}</span>
                <span className="dtk-strip-state-name">{STATE_SHORT[stateNum]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Board — horizontal scroll, one column per state ──── */}
      <div className="dtk-board-scroll" ref={boardRef}>
        <div className="dtk-board">
          {Array.from({ length: 11 }, (_, i) => i + 1).map(stateNum => {
            const items = byState[stateNum];
            const phase = STATE_PHASE[stateNum];
            return (
              <section
                key={stateNum}
                className={`dtk-col dtk-phase-${phase}`}
                data-state={stateNum}
              >
                <header className="dtk-col-header">
                  <span className="dtk-col-state-num">{stateNum}</span>
                  <span className="dtk-col-title">{STATE_NAMES[stateNum]}</span>
                  <span className="dtk-col-count">{items.length}</span>
                </header>

                {items.length === 0 ? (
                  <div className="dtk-col-empty">—</div>
                ) : (
                  <div className="dtk-cards">
                    {items.map(dt => (
                      <DTCard
                        key={dt._id}
                        dt={dt}
                        onClick={() => navigate(`/project/${projectId}/dt/${dt._id}`)}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DTCard({ dt, onClick }) {
  const isCancelled = dt.procedureState === 11;
  const isClosed = dt.procedureState === 10;
  return (
    <button
      className={`dtk-card dtk-card-urg-${dt.urgency} ${isCancelled ? 'is-cancelled' : ''} ${isClosed ? 'is-closed' : ''}`}
      onClick={onClick}
    >
      <div className="dtk-card-top">
        <span className="dtk-card-ident">{dt.identifier}</span>
        {dt.urgency !== 'routine' && (
          <span className={`dtk-card-urgflag dtk-urg-${dt.urgency}`}>
            {dt.urgency === 'safety-critical' ? '⚠ Safety' : 'Exp.'}
          </span>
        )}
      </div>
      <div className="dtk-card-title">{dt.title}</div>
      <div className="dtk-card-bottom">
        {dt.assignedAuthor ? (
          <span className="dtk-card-author" title={`Author: ${dt.assignedAuthor.name || dt.assignedAuthor.email}`}>
            <UserAvatar user={dt.assignedAuthor} className="dtk-avatar" />
            <span className="dtk-author-name">{dt.assignedAuthor.name || dt.assignedAuthor.email}</span>
          </span>
        ) : (
          <span className="dtk-card-noauthor">
            <FileText size={11} /> Author pending
          </span>
        )}
        {dt.createdAt && (
          <span className="dtk-card-date">
            <Clock size={10} />
            {new Date(dt.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    </button>
  );
}

export default DesignTaskKanban;
